import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/StatusBadge";
import { ThumbnailUploader } from "@/components/ThumbnailUploader";
import { VimeoUrlField } from "@/components/VimeoUrlField";
import { VideoThumbnail } from "@/components/VideoThumbnail";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Edit, Plus, Trash2, Layers } from "lucide-react";
import { BatchAccessField } from "@/components/BatchAccessField";
import api, { thumbUrl } from "@/lib/api";
import { isVimeo, isYouTube } from "@/lib/videoUrl";
import { toast } from "sonner";

function videoSourceLabel(url) {
  if (!url) return { label: "None", tone: "muted" };
  if (isVimeo(url)) return { label: "Vimeo", tone: "teal" };
  if (isYouTube(url)) return { label: "YouTube", tone: "teal" };
  if (/^\/api\/files\//.test(url) || /^https?:\/\/.*\/api\/files\//.test(url)) return { label: "Internal", tone: "teal" };
  return { label: "URL", tone: "teal" };
}

const EMPTY_PART = {
  id: null,
  code_suffix: "",
  title: "",
  description: "",
  video_url: "",
  thumbnail_image: "",
  status: "belum_dimuat_naik",
  published: false,
};

export default function AdminModules() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Parts management
  const [partsOpen, setPartsOpen] = useState(false);
  const [activeMod, setActiveMod] = useState(null);
  const [partOpen, setPartOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);

  const load = () => api.get("/modules").then((r) => setItems(r.data));
  const [batches, setBatches] = useState([]);
  useEffect(() => {
    load();
    api.get("/admin/batches").then((r) => setBatches(r.data)).catch(() => setBatches([]));
  }, []);

  const openEdit = (m) => {
    setEditing({
      ...m,
      visibility: m.visibility || "all_students",
      allowed_batches: m.allowed_batches || [],
    });
    setOpen(true);
  };
  const openParts = (m) => { setActiveMod(m); setPartsOpen(true); };

  const save = async () => {
    try {
      const { id, code, title, description, content, video_url, thumbnail_image, status, published, order, visibility, allowed_batches } = editing;
      await api.patch(`/admin/modules/${id}`, {
        code, title, description, content, video_url, thumbnail_image, status, published, order,
        visibility: visibility || "all_students",
        allowed_batches: allowed_batches || [],
      });
      toast.success("Module updated");
      setOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save";
      toast.error(typeof msg === "string" ? msg : "Failed to save");
    }
  };

  // ---- Parts ----
  const refreshActiveMod = async () => {
    if (!activeMod) return;
    const { data } = await api.get(`/modules/${activeMod.id}`);
    setActiveMod(data);
    load();
  };

  const openCreatePart = () => {
    const next = (activeMod?.parts?.length || 0) + 1;
    setEditingPart({ ...EMPTY_PART, code_suffix: `_${next}` });
    setPartOpen(true);
  };
  const openEditPart = (p) => {
    setEditingPart({
      ...p,
      visibility: p.visibility || "all_students",
      allowed_batches: p.allowed_batches || [],
    });
    setPartOpen(true);
  };

  const savePart = async () => {
    if (!editingPart.title?.trim()) {
      toast.error("Please isi tajuk part.");
      return;
    }
    try {
      const payload = {
        title: editingPart.title,
        description: editingPart.description,
        video_url: editingPart.video_url,
        thumbnail_image: editingPart.thumbnail_image,
        status: editingPart.status,
        published: editingPart.published,
        visibility: editingPart.visibility || "all_students",
        allowed_batches: editingPart.allowed_batches || [],
      };
      if (editingPart.id) {
        await api.patch(`/admin/modules/${activeMod.id}/parts/${editingPart.id}`, payload);
        toast.success("Part updated");
      } else {
        await api.post(`/admin/modules/${activeMod.id}/parts`, {
          ...payload,
          code_suffix: editingPart.code_suffix || undefined,
        });
        toast.success("Part added");
      }
      setPartOpen(false);
      await refreshActiveMod();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save";
      toast.error(typeof msg === "string" ? msg : "Failed to save");
    }
  };

  const deletePart = async (p) => {
    if (!window.confirm(`Delete ${p.code}? This action cannot be undone.`)) return;
    try {
      await api.delete(`/admin/modules/${activeMod.id}/parts/${p.id}`);
      toast.success("Part deleted");
      await refreshActiveMod();
    } catch { toast.error("Gagal padam"); }
  };

  return (
    <AdminLayout title="Learning Modules">
      <div className="mb-5">
        <h1 className="text-3xl font-heading font-bold tracking-tight">Learning Modules</h1>
        <p className="mt-1 text-muted-foreground">Manage modules SC101–SC106. Each module can have multiple video parts.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((m) => (
          <Card key={m.id} className="glass-card" data-testid={`admin-mod-${m.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="text-xs font-mono font-semibold text-primary">{m.code}</span>
                  <h3 className="font-heading font-semibold mt-1">{m.title}</h3>
                </div>
                <StatusBadge status={m.status} />
              </div>
              <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{m.description}</p>
              <div className="mt-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Layers className="h-3 w-3" /> {(m.parts?.length || 0)} bahagian video
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(m)} data-testid={`admin-mod-edit-${m.id}`}>
                  <Edit className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button size="sm" onClick={() => openParts(m)} data-testid={`admin-mod-parts-${m.id}`}>
                  <Layers className="h-3.5 w-3.5" /> Parts
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Module dialog */}
      {editing && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit {editing.code}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Code</Label><Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} /></div>
                <div><Label>Order</Label><Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value, 10) })} /></div>
              </div>
              <div><Label>Title</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div><Label>Content</Label><Textarea rows={6} value={editing.content || ""} onChange={(e) => setEditing({ ...editing, content: e.target.value })} placeholder="Kandungan modul..." /></div>
              <ThumbnailUploader value={editing.thumbnail_image} onChange={(v) => setEditing({ ...editing, thumbnail_image: v })} kind="thumbnail" label="Module Cover Thumbnail" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="belum_dimuat_naik">Not uploaded yet</SelectItem>
                      <SelectItem value="tersedia">Available</SelectItem>
                      <SelectItem value="disembunyikan">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} id="modpub" />
                  <Label htmlFor="modpub">Published</Label>
                </div>
              </div>

              <BatchAccessField
                visibility={editing.visibility}
                allowedBatches={editing.allowed_batches}
                batches={batches}
                onChange={({ visibility, allowed_batches }) => setEditing({ ...editing, visibility, allowed_batches })}
                contentLabel="module"
                testId="mod-batch-access"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-mod-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Parts management dialog */}
      {activeMod && (
        <Dialog open={partsOpen} onOpenChange={setPartsOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Video Parts — {activeMod.code}</DialogTitle>
              <DialogDescription>Setiap bahagian boleh ada Vimeo URL & thumbnail tersendiri.</DialogDescription>
            </DialogHeader>

            <div className="flex justify-end mb-3">
              <Button onClick={openCreatePart} data-testid="add-part-btn" className="btn-glow">
                <Plus className="h-4 w-4" /> Add Part
              </Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {(activeMod.parts || []).length === 0 ? (
                <div className="col-span-full text-center py-8 text-sm text-muted-foreground">
                  No parts yet. Click <strong>Add Part</strong> to start.
                </div>
              ) : (
                (activeMod.parts || []).map((p) => (
                  <Card key={p.id} className="glass-card overflow-hidden" data-testid={`part-card-${p.id}`}>
                    <VideoThumbnail
                      thumbnailUrl={thumbUrl(p.thumbnail_image, p.updated_at)}
                      hasVideo={!!p.video_url}
                      status={p.status}
                    />
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-xs font-mono font-semibold text-primary">{p.code}</span>
                          <h4 className="font-medium text-sm truncate">{p.title}</h4>
                        </div>
                        <StatusBadge status={p.status} />
                      </div>
                      <div className="text-xs space-y-0.5">
                        <div>Sumber: <span className={p.video_url ? `text-${videoSourceLabel(p.video_url).tone}` : "text-muted-foreground"}>{videoSourceLabel(p.video_url).label}</span></div>
                        <div>Thumbnail: <span className={p.thumbnail_image ? "text-teal" : "text-muted-foreground"}>{p.thumbnail_image ? "Yes" : "None"}</span></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <Button size="sm" variant="outline" onClick={() => openEditPart(p)} data-testid={`edit-part-${p.id}`}>
                          <Edit className="h-3.5 w-3.5" /> Edit
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deletePart(p)} data-testid={`delete-part-${p.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Part dialog */}
      {editingPart && activeMod && (
        <Dialog open={partOpen} onOpenChange={setPartOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPart.id ? `Edit ${editingPart.code}` : `Add New Part ${activeMod.code}`}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {!editingPart.id && (
                <div>
                  <Label>Code Suffix</Label>
                  <Input
                    value={editingPart.code_suffix}
                    onChange={(e) => setEditingPart({ ...editingPart, code_suffix: e.target.value })}
                    placeholder="_1"
                    data-testid="part-suffix-input"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Final code will be: <strong>{activeMod.code}{editingPart.code_suffix || `_${(activeMod.parts?.length || 0) + 1}`}</strong>
                  </p>
                </div>
              )}
              <div>
                <Label>Title</Label>
                <Input
                  value={editingPart.title}
                  onChange={(e) => setEditingPart({ ...editingPart, title: e.target.value })}
                  placeholder="Tajuk bahagian"
                  data-testid="part-title-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={2} value={editingPart.description || ""} onChange={(e) => setEditingPart({ ...editingPart, description: e.target.value })} />
              </div>

              <div className="rounded-lg border border-border p-4 bg-muted/20 space-y-4">
                <p className="text-sm font-medium">Sumber Video</p>
                <VimeoUrlField
                  value={editingPart.video_url}
                  onChange={(v) => setEditingPart({ ...editingPart, video_url: v })}
                  testId="part-vimeo-field"
                />
              </div>

              <ThumbnailUploader
                value={editingPart.thumbnail_image}
                onChange={(v) => setEditingPart({ ...editingPart, thumbnail_image: v })}
                kind="thumbnail"
                label="Part Thumbnail"
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={editingPart.status} onValueChange={(v) => setEditingPart({ ...editingPart, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="belum_dimuat_naik">Not uploaded yet</SelectItem>
                      <SelectItem value="tersedia">Available</SelectItem>
                      <SelectItem value="disembunyikan">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={editingPart.published} onCheckedChange={(v) => setEditingPart({ ...editingPart, published: v })} id="partpub" />
                  <Label htmlFor="partpub">Published</Label>
                </div>
              </div>

              <BatchAccessField
                visibility={editingPart.visibility}
                allowedBatches={editingPart.allowed_batches}
                batches={batches}
                onChange={({ visibility, allowed_batches }) => setEditingPart({ ...editingPart, visibility, allowed_batches })}
                contentLabel="part"
                testId="part-batch-access"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPartOpen(false)}>Cancel</Button>
              <Button onClick={savePart} data-testid="save-part-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AdminLayout>
  );
}
